import React from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Lock, Check } from 'lucide-react';
import { legalPaths } from '../data/company';
import {
  ALL_ACCEPTED,
  CATEGORIES,
  CONSENT_TTL_DAYS,
  browserOptOutSignal,
  ConsentChoices,
  ConsentRecord,
  DEFAULT_CHOICES,
  OPEN_SETTINGS_EVENT,
  formatConsentDate,
  loadConsent,
  saveConsent,
} from '../lib/consent';

type View = 'hidden' | 'banner' | 'panel';

/**
 * Consent notice built for the EU (GDPR Art. 6(1)(a) + ePrivacy / Italian
 * Cookie Guidelines): granular categories, everything non-essential off by
 * default, accepting and refusing given equal weight, and a permanent route
 * back to change or withdraw the choice from the footer.
 */
const CookieConsent: React.FC = () => {
  const [view, setView] = React.useState<View>('hidden');
  const [choices, setChoices] = React.useState<ConsentChoices>(DEFAULT_CHOICES);
  const [record, setRecord] = React.useState<ConsentRecord | null>(null);
  const [optOutSignal, setOptOutSignal] = React.useState(false);
  const panelRef = React.useRef<HTMLDivElement>(null);

  // First paint: show the notice only when there is no valid, unexpired record
  // and the browser is not already telling us to opt out.
  React.useEffect(() => {
    const existing = loadConsent();
    const signalled = browserOptOutSignal();
    setRecord(existing);
    setOptOutSignal(signalled);

    if (existing) {
      setChoices(existing.choices);
      return;
    }
    if (signalled) {
      // Honoured as a refusal: nothing optional is set, and we stay out of the way.
      return;
    }
    const timer = window.setTimeout(() => setView('banner'), 700);
    return () => window.clearTimeout(timer);
  }, []);

  // Footer "Cookie settings" reopens the preference panel at any time.
  React.useEffect(() => {
    const open = () => {
      const existing = loadConsent();
      setRecord(existing);
      setChoices(existing?.choices ?? DEFAULT_CHOICES);
      setView('panel');
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);

  // Keep the page behind the modal still, and close on Escape.
  React.useEffect(() => {
    if (view !== 'panel') return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setView(record ? 'hidden' : 'banner');
    };
    window.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [view, record]);

  const commit = (next: ConsentChoices, method: 'accept_all' | 'reject_all' | 'custom') => {
    const saved = saveConsent(next, method);
    setRecord(saved);
    setChoices(saved.choices);
    setView('hidden');
  };

  const acceptAll = () => commit(ALL_ACCEPTED, 'accept_all');
  const rejectAll = () => commit(DEFAULT_CHOICES, 'reject_all');
  const saveChoices = () => commit(choices, 'custom');

  const toggle = (id: keyof ConsentChoices) =>
    setChoices((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <>
      {view === 'banner' && (
        <div
          className="enter fixed inset-x-4 bottom-4 z-[70] sm:inset-x-6 sm:bottom-6 lg:left-8 lg:right-auto lg:max-w-[520px]"
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-body"
        >
          <div className="surface-solid rounded-2xl p-6 shadow-lift sm:p-7">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-peri-400/12 text-peri-300">
                <Cookie size={17} strokeWidth={1.6} />
              </span>
              <div>
                <h2
                  id="cookie-banner-title"
                  className="font-display text-[23px] leading-tight text-paper"
                >
                  Your choice on cookies
                </h2>
                <p id="cookie-banner-body" className="mt-2.5 text-[14px] leading-relaxed text-paper/60">
                  We use strictly necessary cookies to make this site work. Anything beyond that —
                  preferences and analytics — is set only if you agree. You can refuse without
                  losing any part of the site, and change your mind whenever you like.
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2.5 sm:flex-row">
              <button
                type="button"
                onClick={acceptAll}
                className="flex-1 rounded-full bg-brand-sweep px-5 py-3 font-mono text-[11px] uppercase tracking-label text-ink-950 transition-transform duration-300 hover:-translate-y-0.5"
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="flex-1 rounded-full border border-white/20 px-5 py-3 font-mono text-[11px] uppercase tracking-label text-paper transition-colors duration-300 hover:border-white/45 hover:bg-white/[0.06]"
              >
                Reject all
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
              <button
                type="button"
                onClick={() => setView('panel')}
                className="font-mono text-[10.5px] uppercase tracking-label text-peri-300 underline decoration-peri-400/40 underline-offset-4 transition-colors hover:text-paper"
              >
                Manage preferences
              </button>
              <Link
                to={legalPaths.cookies}
                className="font-mono text-[10.5px] uppercase tracking-label text-paper/55 transition-colors hover:text-paper"
              >
                Cookie Policy
              </Link>
              <Link
                to={legalPaths.privacy}
                className="font-mono text-[10.5px] uppercase tracking-label text-paper/55 transition-colors hover:text-paper"
              >
                Privacy
              </Link>
            </div>
          </div>
        </div>
      )}

      {view === 'panel' && (
        <div className="enter-overlay fixed inset-0 z-[80] flex items-end justify-center bg-ink-950/80 p-4 backdrop-blur-sm sm:items-center sm:p-6">
          <div
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-panel-title"
            className="enter-panel max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-white/12 bg-ink-900 p-6 shadow-lift outline-none sm:p-9"
          >
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="label">Privacy preferences</p>
                <h2
                  id="cookie-panel-title"
                  className="mt-3 font-display text-[30px] leading-tight text-paper sm:text-[34px]"
                >
                  Choose what you allow
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setView(record ? 'hidden' : 'banner')}
                aria-label="Close preferences"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/12 text-paper/60 transition-colors hover:border-white/35 hover:text-paper"
              >
                <X size={16} strokeWidth={1.6} />
              </button>
            </div>

            <p className="mt-4 text-[14px] leading-relaxed text-paper/60">
              Non-essential categories are off unless you turn them on. Your choice is stored for{' '}
              {CONSENT_TTL_DAYS} days, after which we will ask again. Full detail lives in our{' '}
              <Link
                to={legalPaths.cookies}
                className="text-peri-300 underline decoration-peri-400/40 underline-offset-4 hover:text-paper"
              >
                Cookie Policy
              </Link>
              .
            </p>

            {optOutSignal && !record && (
              <p className="mt-5 rounded-xl border border-peri-400/25 bg-peri-400/[0.07] px-5 py-4 text-[13.5px] leading-relaxed text-paper/70">
                Your browser is sending an opt-out signal (Global Privacy Control or Do Not
                Track), so we are treating every optional category as refused. Saving a choice
                here overrides that for this site.
              </p>
            )}

            <div className="mt-7 space-y-3">
              {CATEGORIES.map((category) => {
                const active = category.locked ? true : choices[category.id];
                return (
                  <div
                    key={category.id}
                    className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
                  >
                    <div className="flex items-start justify-between gap-5">
                      <div>
                        <div className="flex items-center gap-2.5">
                          <h3 className="text-[15.5px] font-semibold text-paper">{category.title}</h3>
                          {category.locked && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-white/12 px-2 py-0.5 font-mono text-[9px] uppercase tracking-label text-paper/55">
                              <Lock size={9} strokeWidth={2} />
                              Always on
                            </span>
                          )}
                        </div>
                        <p className="mt-2 text-[13.5px] leading-relaxed text-paper/55">
                          {category.summary}
                        </p>
                        <p className="mt-2 font-mono text-[10.5px] uppercase tracking-label text-paper/55">
                          {category.examples}
                        </p>
                      </div>

                      <button
                        type="button"
                        role="switch"
                        aria-checked={active}
                        aria-label={`${category.title} cookies`}
                        disabled={category.locked}
                        onClick={() => !category.locked && toggle(category.id)}
                        className={`relative mt-1 h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${
                          active ? 'bg-peri-500' : 'bg-white/12'
                        } ${category.locked ? 'cursor-not-allowed opacity-55' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`absolute top-1 h-4 w-4 rounded-full bg-paper transition-all duration-300 ${
                            active ? 'left-6' : 'left-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sticky bottom-0 -mx-6 mt-8 flex flex-col gap-2.5 border-t border-white/10 bg-ink-900/95 px-6 py-5 backdrop-blur sm:-mx-9 sm:flex-row sm:px-9">
              <button
                type="button"
                onClick={saveChoices}
                className="flex-1 rounded-full bg-brand-sweep px-5 py-3 font-mono text-[11px] uppercase tracking-label text-ink-950 transition-transform duration-300 hover:-translate-y-0.5"
              >
                Save my choices
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="flex-1 rounded-full border border-white/20 px-5 py-3 font-mono text-[11px] uppercase tracking-label text-paper transition-colors hover:border-white/45 hover:bg-white/[0.06]"
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="flex-1 rounded-full border border-white/20 px-5 py-3 font-mono text-[11px] uppercase tracking-label text-paper transition-colors hover:border-white/45 hover:bg-white/[0.06]"
              >
                Reject all
              </button>
            </div>

            {record && (
              <p className="mt-5 flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-label text-paper/55">
                <Check size={11} strokeWidth={2} className="text-peri-400" />
                Choice recorded {formatConsentDate(record.savedAt)}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
